/**
 * The organic boundary where the page meets the closing surface.
 *
 * Slow, soft pieces rise from the bottom of the closing surface, sway a little, and dissolve into
 * it before they arrive. The same pieces push the boundary upward as they pass under it, so the
 * edge between page and surface moves with them instead of sitting on a straight cut.
 *
 * ── One field, two canvases ────────────────────────────────────────────────────────────────────
 *
 * The closing surface is one continuous colour, but on templates with a closing band it is built
 * from two sections that are not DOM siblings: the band is inside <main>, the footer is inside
 * <footer>. A single overlay spanning both would have to live in one of them, and neither works —
 * from the footer it paints over the band's text, and pushed behind that text with a negative
 * z-index it also goes behind the band's own background and disappears.
 *
 * So the geometry is shared instead of the element. Every participating section renders its own
 * canvas, and one field measures their union and draws all of them in union coordinates. A piece
 * crossing the seam is one piece at one position drawn twice, not two pieces handed off — nothing
 * resets, and the boundary exists once, at the top of the union. The lower canvas is simply the
 * part of the field where the surface path is far overhead, which is why it fills solid with no
 * seam of its own and still carries the pieces rising through it.
 *
 * Sections that are not vertically adjacent do not share a field. That is what makes an editor
 * reorder safe: move the band away from the footer and each becomes its own surface, rather than
 * one field stretching across whatever ended up between them.
 *
 * ── Failure is a straight edge, never an unpainted band ────────────────────────────────────────
 *
 * Each canvas paints both sides of the boundary opaquely: `above` over the page side, `surface`
 * below. The sections keep their own backgrounds, so no canvas, no JS, or anything thrown leaves
 * the straight edge that ships today rather than a hole with white text on white.
 */

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

/** Largest gap, in px, across which two sections still count as one continuous surface. */
const SEAM_TOLERANCE = 4;

/** Smoothstep. Used for every fade, so nothing enters or leaves on a hard edge. */
const smooth = (v) => {
  const t = Math.max(0, Math.min(1, v));
  return t * t * (3 - 2 * t);
};

const clamp = (value, min, max, fallback) => {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? Math.max(min, Math.min(max, n)) : fallback;
};

const parseHex = (value) => {
  const hex = String(value || '').trim().replace('#', '');
  const full = hex.length === 3 ? hex.replace(/./g, (c) => c + c) : hex;
  if (!/^[0-9a-f]{6}$/i.test(full)) return null;
  return [0, 2, 4].map((i) => Number.parseInt(full.slice(i, i + 2), 16));
};

/**
 * Whether dark appearance is currently applied.
 *
 * The same two signals the stylesheet reads, in the same order: an explicit choice on the root
 * element wins, and System falls through to the operating system.
 */
const inDarkAppearance = () => {
  const explicit = document.documentElement.getAttribute('data-timecard-appearance');
  if (explicit === 'dark') return true;
  if (explicit === 'light') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

/**
 * The ground actually painted above this surface.
 *
 * `data-above` is a colour a merchant configured for the light storefront, and it stays right
 * there. In dark appearance it is not: the closing surface was rendering a white strip between a
 * charcoal page and the red footer, because the configured white was still being painted over the
 * page side of the boundary.
 *
 * Asked of the DOM rather than derived. The section immediately above is the thing whose colour
 * has to be matched, and in dark every light-grounded section resolves to the same page ground —
 * so the body is the fallback when that neighbour paints nothing of its own.
 */
const groundAbove = (host) => {
  const section = host.closest('.shopify-section') ?? host.parentElement;

  /**
   * Only an explicit zero ALPHA makes a colour transparent.
   *
   * A pattern matching a trailing `, 0)` also matches `rgb(0, 0, 0)` — opaque black, and a
   * perfectly ordinary dark ground — which would have been skipped as if it painted nothing. The
   * fourth component is the only one that can mean transparent, so it is the only one read.
   */
  const isTransparent = (colour) => {
    if (!colour || colour === 'transparent') return true;
    const parsed = colour.match(/^rgba?\(([^)]+)\)$/);
    if (!parsed) return false;
    const parts = parsed[1].split(/[,/]/).map((part) => part.trim());
    return parts.length > 3 && Number.parseFloat(parts[3]) === 0;
  };

  const opaque = (element) => {
    if (!element) return null;
    const colour = getComputedStyle(element).backgroundColor;
    return isTransparent(colour) ? null : colour;
  };

  return opaque(section?.previousElementSibling) ?? opaque(document.body) ?? null;
};

const mix = (rgb, target, amount) =>
  `rgb(${rgb.map((c) => Math.round(c + (target - c) * amount)).join(',')})`;

/** WCAG relative luminance. Used to decide which way the tones may move, not to style anything. */
const luminance = (rgb) => {
  const [r, g, b] = rgb.map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/**
 * Which way a tone is allowed to move, given the text that sits on this surface.
 *
 * A piece drawn over the surface changes the effective background behind whatever text it passes
 * under, so its colour is a contrast decision, not a decorative one. Timecard red carries white
 * text at 4.57:1 — a sixteenth above the 4.5:1 floor — so a light tone at any usable alpha spends
 * more than the whole margin; the mockup's pink measures 4.17:1 at its own alpha.
 *
 * Rather than tuning an alpha until it passes, tones move AWAY from the text colour: darker under
 * light text, lighter under dark text. The composite then sits between the base colour and the far
 * end from the text, so contrast can only rise above the base pair's — at every intensity setting,
 * for any surface a merchant picks, with no value left to get wrong.
 */
const toneTarget = (surface, ink) => (luminance(ink) > luminance(surface) ? 0 : 255);

/* ══════════════════════════════════════════════════════════════════ the shared field ═════ */

const members = new Set();

const field = {
  running: false,
  frame: 0,
  last: 0,
  // No clock here on purpose: each run keeps its own, because two surfaces can run at two speeds.
  runs: [],

  start() {
    if (this.running) return;
    this.running = true;

    this.motionQuery = window.matchMedia(REDUCED_MOTION);
    this.onMotion = () => this.draw();
    this.onVisibility = () => {
      if (!document.hidden) this.last = 0;
    };

    this.motionQuery.addEventListener('change', this.onMotion);
    document.addEventListener('visibilitychange', this.onVisibility);

    // No window resize listener: a viewport change resizes the members themselves, which the
    // observer below already reports. A second source would only re-measure the same thing twice.
    this.resizer = new ResizeObserver(() => this.measure());
    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) entry.target.onScreen = entry.isIntersecting;
    });

    for (const member of members) this.watch(member);

    this.tick = this.tick.bind(this);
    this.measure();
    this.frame = requestAnimationFrame(this.tick);
  },

  stop() {
    if (!this.running) return;
    this.running = false;
    cancelAnimationFrame(this.frame);
    this.resizer.disconnect();
    this.observer.disconnect();
    this.motionQuery.removeEventListener('change', this.onMotion);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.runs = [];
  },

  watch(member) {
    this.resizer.observe(member);
    this.observer.observe(member);
  },

  unwatch(member) {
    if (!this.running) return;
    this.resizer.unobserve(member);
    this.observer.unobserve(member);
  },

  /**
   * Group the members into continuous surfaces and give each run one coordinate space.
   *
   * Document coordinates, not viewport ones: the relationship between two sections does not change
   * when the page scrolls, and measuring in document space keeps it that way.
   */
  measure() {
    const scrollY = window.scrollY || 0;
    const boxes = [...members]
      .map((el) => {
        // An element that cannot report a box yet still has its own dimensions, and a lone surface
        // needs no document position to be drawn correctly — it is the whole run.
        const rect = el.getBoundingClientRect
          ? el.getBoundingClientRect()
          : { top: 0, bottom: el.clientHeight, width: el.clientWidth };
        const d = el.dataset;
        const key = `${String(d.surface).toLowerCase()}|${String(d.ink).toLowerCase()}`;
        return { el, key, top: rect.top + scrollY, bottom: rect.bottom + scrollY, width: rect.width };
      })
      .filter((box) => box.width > 0 && box.bottom > box.top)
      .sort((a, b) => a.top - b.top);

    const runs = [];
    for (const box of boxes) {
      const run = runs[runs.length - 1];
      // Adjacent sections in the same colours are one surface; anything else is its own.
      //
      // The colour test is not a refinement, it is the Halloween case. There the closing band is
      // dark with cream text while the footer below it is cream with dark text — two surfaces that
      // happen to touch. Grouping on adjacency alone would paint the band's dark ground across the
      // footer, putting its dark text on a dark field. Matching colours is what makes "one
      // continuous surface" mean the same thing to the code as it does to the eye.
      if (run && box.top - run.bottom <= SEAM_TOLERANCE && run.key === box.key) {
        run.bottom = Math.max(run.bottom, box.bottom);
        run.width = Math.max(run.width, box.width);
        run.boxes.push(box);
      } else {
        runs.push({ key: box.key, top: box.top, bottom: box.bottom, width: box.width, boxes: [box] });
      }
    }

    for (const run of runs) {
      run.height = run.bottom - run.top;
      // The section that starts the surface owns its look: it is the one with a boundary to draw,
      // and the one whose settings — colours, speed, motion switch — govern the whole run.
      run.lead = run.boxes[0].el;
      run.settings = run.lead.settings;
      // Its own clock, carried on the lead across re-measurements so a resize does not rewind the
      // surface. Two runs at different speeds cannot share one clock, and on the Halloween route
      // there genuinely are two.
      run.time = run.lead.surfaceTime === undefined ? 11 : run.lead.surfaceTime;
      for (const box of run.boxes) {
        box.offset = box.top - run.top;
        box.el.resizeCanvas();
        // Published so the section can reserve room for a boundary it turns out to be drawing.
        // Liquid has to guess this from the page type, and the guess is wrong the moment someone
        // removes the closing band from a template that had one; measurement is not a guess.
        box.el.markLead(box.el === run.lead);
      }
    }

    this.runs = runs;
    this.draw();
  },

  /**
   * Whether one surface is moving.
   *
   * Per run, and read from that run's lead. Asking whether ANY member wants motion was a real bug:
   * the homepage band owns its surface, but the footer continuing that surface carries a motion
   * setting of its own, so switching the band off left the footer voting yes and the animation ran
   * anyway. The lead owns the run; the settings on a continuing member do not get a say.
   */
  runPlaying(run) {
    return !this.motionQuery.matches && run.lead.dataset.motion !== 'off';
  },

  runVisible(run) {
    return !document.hidden && run.boxes.some((box) => box.el.onScreen);
  },

  draw() {
    for (const run of this.runs) {
      if (run.settings) this.drawRun(run);
    }
  },

  /** Every canvas in a run is painted from one generated frame, so they cannot drift apart. */
  drawRun(run) {
    const pieces = this.pieces(run, run.settings);
    for (const box of run.boxes) box.el.paint(run, pieces, run.settings, box.offset);
  },

  /**
   * Every piece is a deterministic function of time and the run's own height, so nothing
   * accumulates drift, and the same piece resolves to the same place in every canvas that draws it.
   */
  pieces(run, s) {
    const count = run.width < 500 ? 5 : 8;
    const list = [];

    for (let i = 0; i < count; i += 1) {
      // Golden-ratio spacing keeps pieces from lining up or clustering as the count changes.
      const seedX = (i * 0.61803398875 + 0.12) % 1;
      const phase = (i * 0.371) % 1;
      const radius = Math.min(run.width * 0.15, (28 + (i % 4) * 13) * s.size);

      // A long, slow cycle each: more than half a minute from first appearance to dissolve. The
      // travel spans the whole run, so a piece rises through the directory and base as one move.
      const cycle = (run.time / (34 + i * 2) + phase) % 1;
      const travel = run.height + 160 - (s.band + 4);
      const y = run.height + 160 - cycle * travel;

      const margin = radius * 1.25 + 22;
      const x = margin + seedX * (run.width - 2 * margin) + 18 * Math.sin(run.time * 0.16 + i);

      // Fades in over the first stretch and out over the last, so a piece is never cut off at an
      // edge and never pops into existence mid-surface.
      const visibility = smooth(cycle / 0.08) * (1 - smooth((cycle - 0.81) / 0.19));

      // Peaks as the piece passes just under the boundary: that is when it pushes the edge.
      const contact = Math.exp(-Math.pow((y - radius - (s.band - 3)) / 72, 2)) * visibility;

      list.push({ x, y, radius, visibility, contact, index: i, phase: run.time * 0.2 + i });
    }

    return list;
  },

  /**
   * One rAF for the page, advancing each surface on its own terms.
   *
   * Runs are independent: their own clock, their own speed, their own motion switch. Halloween has
   * two of them and either can be stopped or slowed without touching the other. Within a run every
   * canvas is painted from a single generated frame, so they stay locked together.
   */
  tick(now) {
    if (!this.running) return;

    // ~40fps. The motion is slow enough that a full 60 buys nothing and costs a third more work.
    if (now - this.last > 24) {
      // Clamped so a backgrounded tab returning after a minute advances one frame, not a jump.
      const elapsed = Math.min((now - this.last) / 1000, 0.08);

      for (const run of this.runs) {
        if (!run.settings) continue;
        if (!this.runPlaying(run) || !this.runVisible(run)) continue;

        run.time += elapsed * run.settings.speed;
        // Kept on the lead so a re-measurement resumes the surface rather than restarting it.
        run.lead.surfaceTime = run.time;
        this.drawRun(run);
      }

      this.last = now;
    }

    this.frame = requestAnimationFrame(this.tick);
  },
};

/* ════════════════════════════════════════════════════════════════════════ the element ═════ */

class TimecardLava extends HTMLElement {
  connectedCallback() {
    this.canvas = this.querySelector('canvas');
    this.ctx = this.canvas && this.canvas.getContext ? this.canvas.getContext('2d') : null;
    if (!this.ctx) return;

    this.readSettings();
    this.onScreen = true;

    members.add(this);
    if (field.running) {
      field.watch(this);
      field.measure();
    } else {
      field.start();
    }
  }

  /**
   * Leaving the document gives the membership back. The theme editor unloads and reloads a section
   * on every change and moves it on reorder; because the field is keyed on live membership rather
   * than on a one-time scan, the surface simply re-forms around whoever is present afterwards.
   */
  disconnectedCallback() {
    if (!members.has(this)) return;
    field.unwatch(this);
    members.delete(this);
    if (members.size === 0) field.stop();
    else field.measure();
  }

  readSettings() {
    const d = this.dataset;
    const surface = parseHex(d.surface);
    const ink = parseHex(d.ink) || [255, 255, 255];

    const next = {
      speed: clamp(d.speed, 0.2, 3, 1),
      size: clamp(d.size, 0.4, 2, 1),
      intensity: clamp(d.intensity, 0, 1, 0.55),
      rise: clamp(d.rise, 0, 80, 44),
      band: clamp(d.band, 16, 240, 72),
      surface,
      surfaceFill: surface ? `rgb(${surface.join(',')})` : null,
      // The page side may be left unset, in which case the page's own background is read rather
      // than guessed. A colour that cannot be parsed is treated the same way.
      //
      // In dark appearance the configured colour is ignored entirely and the real ground above is
      // read instead: `data-above` describes the light storefront, and painting it here put a
      // white strip between a charcoal page and the red footer. Light is untouched.
      aboveFill: inDarkAppearance()
        ? groundAbove(this) ?? getComputedStyle(document.body).backgroundColor
        : parseHex(d.above)
          ? `rgb(${parseHex(d.above).join(',')})`
          : getComputedStyle(document.body).backgroundColor,
      // Two depths of the same safe direction: enough separation to read as distinct masses, with
      // neither able to move the background toward the text colour. See toneTarget above.
      toneA: surface ? mix(surface, toneTarget(surface, ink), 0.14) : null,
      toneB: surface ? mix(surface, toneTarget(surface, ink), 0.3) : null,
    };

    /*
     * Updated in place, never replaced.
     *
     * `measure()` caches this object on the run it leads — `run.settings = run.lead.settings` — and
     * `paint` is handed that cached reference on every frame. Assigning a fresh object here left
     * the run holding the old one, so a re-read produced no visible change until the next resize
     * rebuilt the runs. Mutating means every holder of the reference sees the new values at once.
     */
    if (this.settings) Object.assign(this.settings, next);
    else this.settings = next;
  }

  /**
   * Tell the surrounding section whether it is the top of a surface.
   *
   * The attribute lands on the parent — the section's own root — because that is the element whose
   * spacing has to change. Guarded, since an element can be measured before it has a parent.
   */
  markLead(isLead) {
    const host = this.parentElement;
    if (!host || !host.toggleAttribute) return;
    host.toggleAttribute('data-lava-lead', isLead);
  }

  resizeCanvas() {
    const width = this.clientWidth;
    const height = this.clientHeight;
    if (!width || !height) return;

    this.width = width;
    this.height = height;

    // Capped so a 3x phone does not allocate a canvas far larger than the detail in it warrants.
    const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    this.canvas.width = Math.round(width * dpr);
    this.canvas.height = Math.round(height * dpr);
    this.dpr = dpr;
  }

  boundaryAt(x, run, pieces, s) {
    let y = s.band + 5 * Math.sin((x / run.width) * 9 + run.time * 0.18);

    for (const piece of pieces) {
      const reach = Math.exp(-Math.pow((x - piece.x) / (40 + piece.radius), 2));
      y -= Math.min(s.rise, piece.radius * 0.7) * piece.contact * reach;
    }

    // Never far enough up to reach the content sitting above the surface.
    return Math.max(12, y);
  }

  surfacePath(run, pieces, s) {
    const path = new Path2D();

    for (let x = 0; x <= run.width + 2; x += 2) {
      const y = this.boundaryAt(x, run, pieces, s);
      if (x) path.lineTo(x, y);
      else path.moveTo(x, y);
    }

    path.lineTo(run.width, run.height);
    path.lineTo(0, run.height);
    path.closePath();
    return path;
  }

  piecePath(piece) {
    const path = new Path2D();

    for (let j = 0; j <= 80; j += 1) {
      const a = (j / 80) * Math.PI * 2;
      // Two out-of-phase harmonics: a soft irregular mass rather than a circle.
      const r = piece.radius * (1 + 0.1 * Math.sin(3 * a + piece.phase) + 0.07 * Math.cos(2 * a - piece.phase));
      const x = piece.x + Math.cos(a) * r;
      const y = piece.y + Math.sin(a) * r * 1.35;
      if (j) path.lineTo(x, y);
      else path.moveTo(x, y);
    }

    path.closePath();
    return path;
  }

  /**
   * Draw this canvas's window onto the run.
   *
   * The whole scene is expressed in run coordinates and the canvas is translated to its own place
   * in it. A canvas below the lead therefore receives the surface path from far above its top edge,
   * fills solid, and shows no boundary of its own — the seam disappears because it was never drawn.
   */
  paint(run, pieces, s, offset) {
    if (!this.ctx || !this.width || !this.height) return;
    const { ctx } = this;

    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, this.width, this.height);

    // The page side is painted first and the surface laid over it, so the boundary is the meeting
    // of two opaque fills rather than an edge drawn on top of one.
    if (s.aboveFill) {
      ctx.fillStyle = s.aboveFill;
      ctx.fillRect(0, 0, this.width, this.height);
    }
    if (!s.surfaceFill) return;

    ctx.translate(0, -offset);

    const edge = this.surfacePath(run, pieces, s);
    ctx.fillStyle = s.surfaceFill;
    ctx.fill(edge);

    ctx.save();
    ctx.clip(edge);

    for (const piece of pieces) {
      // Dissolved out before the piece can touch the boundary it is pushing. Without this a piece
      // arrives at the edge and gets sliced by the clip, which reads as a shape cut off at a line
      // rather than as something melting into the surface.
      const merge = smooth((piece.y - piece.radius * 1.6 - s.band) / 100);
      ctx.globalAlpha = s.intensity * 0.3 * piece.visibility * merge;
      ctx.fillStyle = piece.index % 2 ? s.toneA : s.toneB;
      ctx.fill(this.piecePath(piece));
    }

    ctx.restore();
    ctx.globalAlpha = 1;
  }
}

/**
 * Re-resolve the page side when the appearance changes.
 *
 * Only the colour is re-read. The field keeps running, no member is re-registered, no geometry is
 * remeasured and nothing about the motion restarts — `paint` reads `settings.aboveFill` on every
 * frame, so the next frame simply uses the new value.
 *
 * Two triggers, matching the two ways appearance can change: the attribute the control writes on
 * the root element, and the operating system flipping while the visitor is on System.
 */
const refreshAboveFill = () => {
  for (const member of members) member.readSettings();

  /*
   * One repaint, because a static surface has no next frame to pick the colour up in.
   *
   * With reduced motion, or `data-motion="off"`, the field draws once and then stops — so a
   * settings change alone would sit invisible until something else forced a paint. `draw` renders
   * each run from its own stored `run.time`; it does not advance a clock, reset one, or restart
   * anything, so calling it here is the same frame again with the new colour.
   */
  field.draw();
};

new MutationObserver(refreshAboveFill).observe(document.documentElement, {
  attributes: true,
  attributeFilter: ['data-timecard-appearance'],
});

window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', refreshAboveFill);

// Defined for its side effect and deliberately exports nothing: the file has to be readable both as
// a browser module and by a plain evaluator, and a bare `export` would make the second impossible.
if (!customElements.get('timecard-lava')) {
  customElements.define('timecard-lava', TimecardLava);
}
